import React from 'react'
import { cn } from '@/lib/utils'
import { BaseFileUpload } from '@/shared/ui'
import { CommunityEmojiIcon } from './CommunityEmojiIcon'
import { loadEmojiMartData, getEmojiNative, searchEmojis, type EmojiMartCategory, type EmojiMartData } from '../utils/emojiMart'
import { useCommunityCustomEmojis } from '../hooks/useCommunityCustomEmojis'
import { buildCommunityCustomEmojiToken } from '../utils/communityEmojiTokens'
import { COMMUNITY_CUSTOM_EMOJI_ACCEPT } from '../utils/communityCustomEmojis'
import type { CommunityCustomEmoji } from '../services/communityCustomEmojiService'

import {
  Clock,
  Flag,
  Hash,
  Leaf,
  Loader2,
  Lightbulb,
  Pizza,
  Plane,
  Search,
  Smile,
  Sparkles,
  Trash2,
  Trophy,
} from 'lucide-react'

export type EmojiPickerTab =
  | 'search'
  | 'recent'
  | 'people'
  | 'nature'
  | 'foods'
  | 'activity'
  | 'places'
  | 'objects'
  | 'symbols'
  | 'flags'
  | 'custom'

type EmojiPickerProps = {
  onSelect: (emoji: string) => void
  onClose?: () => void
  className?: string
  initialTab?: EmojiPickerTab
  canDeleteCustomEmojis?: boolean
}

const RECENTS_KEY = 'community:emoji:recents:v1'
const RECENTS_MAX = 40

function readRecents(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.map((x) => String(x || '')).filter(Boolean)
  } catch {
    return []
  }
}

function writeRecents(list: string[]) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)))
  } catch {
    // ignore
  }
}

function trackRecent(next: string) {
  const value = String(next || '').trim()
  if (!value) return
  const current = readRecents()
  const dedup = [value, ...current.filter((x) => x !== value)]
  writeRecents(dedup)
}

type EmojiPickerTabConfig = {
  id: EmojiPickerTab
  label: string
  icon: React.ComponentType<{ className?: string }>
}

const TABS: EmojiPickerTabConfig[] = [
  { id: 'search', label: 'Buscar', icon: Search },
  { id: 'recent', label: 'Usados', icon: Clock },
  { id: 'people', label: 'Caras e pessoas', icon: Smile },
  { id: 'nature', label: 'Animais e natureza', icon: Leaf },
  { id: 'foods', label: 'Comida e bebida', icon: Pizza },
  { id: 'activity', label: 'Atividades', icon: Trophy },
  { id: 'places', label: 'Viagens e lugares', icon: Plane },
  { id: 'objects', label: 'Objetos', icon: Lightbulb },
  { id: 'symbols', label: 'Símbolos', icon: Hash },
  { id: 'flags', label: 'Bandeiras', icon: Flag },
  { id: 'custom', label: 'Personalizados', icon: Sparkles },
]

function resolveCategoryId(tab: EmojiPickerTab): string | null {
  if (tab === 'people') return 'people'
  if (tab === 'nature') return 'nature'
  if (tab === 'foods') return 'foods'
  if (tab === 'activity') return 'activity'
  if (tab === 'places') return 'places'
  if (tab === 'objects') return 'objects'
  if (tab === 'symbols') return 'symbols'
  if (tab === 'flags') return 'flags'
  return null
}

function findCategory(data: EmojiMartData, tab: EmojiPickerTab): EmojiMartCategory | null {
  const categoryId = resolveCategoryId(tab)
  if (!categoryId) return null
  const cats = Array.isArray(data?.categories) ? data.categories : []
  return cats.find((c) => String(c?.id || '') === categoryId) || null
}

function buildEmojiListFromCategory(data: EmojiMartData, category: EmojiMartCategory): string[] {
  const ids = Array.isArray(category?.emojis) ? category.emojis : []
  return ids
    .map((id) => getEmojiNative(data, String(id || '')))
    .filter((x): x is string => !!x)
}

function EmojiButton({
  value,
  onPick,
  onDelete,
  deleting,
}: {
  value: string
  onPick: (emoji: string) => void
  onDelete?: () => void
  deleting?: boolean
}) {
  return (
    <div className="relative">
      <button
        type="button"
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded text-[22px] hover:bg-[#1f1f1f]',
          deleting && 'opacity-60',
        )}
        onClick={() => onPick(value)}
        aria-label="Selecionar emoji"
        disabled={!!deleting}
      >
        <CommunityEmojiIcon emoji={value} showFallback />
      </button>

      {onDelete && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onDelete()
          }}
          className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
          aria-label="Remover emoji personalizado"
          disabled={!!deleting}
        >
          {deleting ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          ) : (
            <Trash2 className="h-3 w-3" aria-hidden="true" />
          )}
        </button>
      )}
    </div>
  )
}

export function EmojiPicker({
  onSelect,
  onClose,
  className,
  initialTab = 'search',
  canDeleteCustomEmojis = false,
}: EmojiPickerProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const [data, setData] = React.useState<EmojiMartData | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [query, setQuery] = React.useState('')
  const [activeTab, setActiveTab] = React.useState<EmojiPickerTab>(initialTab)
  const [recents, setRecents] = React.useState<string[]>(() => readRecents())
  const custom = useCommunityCustomEmojis()
  const customTokens = React.useMemo(
    () =>
      (custom.emojis || [])
        .map((e) => buildCommunityCustomEmojiToken(e.id))
        .filter(Boolean),
    [custom.emojis],
  )
  const customByToken = React.useMemo(() => {
    const map = new Map<string, CommunityCustomEmoji>()
    ;(custom.emojis || []).forEach((emoji) => {
      const token = buildCommunityCustomEmojiToken(emoji.id)
      if (!token) return
      map.set(token, emoji)
    })
    return map
  }, [custom.emojis])

  React.useEffect(() => {
    let cancelled = false
    setLoading(true)
    loadEmojiMartData()
      .then((next) => {
        if (cancelled) return
        setData(next)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setData(null)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  React.useEffect(() => {
    if (!onClose) return

    const onDoc = (ev: MouseEvent) => {
      if (!rootRef.current) return
      const target = ev.target
      if (target instanceof Node && rootRef.current.contains(target)) return
      onClose()
    }

    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [onClose])

  const handlePick = React.useCallback(
    (emoji: string) => {
      const trimmed = String(emoji || '').trim()
      if (!trimmed) return
      trackRecent(trimmed)
      setRecents(readRecents())
      onSelect(trimmed)
    },
    [onSelect],
  )

  const searchResults = React.useMemo(() => {
    if (!data) return []
    const ids = searchEmojis(data, query, 160)
    return ids
      .map((id) => getEmojiNative(data, String(id || '')))
      .filter((x): x is string => !!x)
  }, [data, query])

  const sections = React.useMemo(() => {
    if (activeTab === 'custom') {
      return [
        {
          title: 'Emojis personalizados',
          emojis: customTokens,
        },
      ]
    }

    if (!data) return [] as { title: string; emojis: string[] }[]

    if (query.trim()) {
      return [
        {
          title: `Resultados (${searchResults.length})`,
          emojis: searchResults,
        },
      ]
    }

    const out: { title: string; emojis: string[] }[] = []

    if (recents.length) {
      out.push({
        title: 'Usado frequentemente',
        emojis: recents.slice(0, 32),
      })
    }

    if (activeTab === 'search') {
      const cats = Array.isArray(data.categories) ? data.categories : []
      cats.forEach((cat) => {
        if (!cat?.id || !cat?.name) return
        const list = buildEmojiListFromCategory(data, cat)
        if (!list.length) return
        out.push({ title: String(cat.name), emojis: list })
      })
      return out
    }

    if (activeTab === 'recent') {
      return out
    }

    const cat = findCategory(data, activeTab)
    if (cat) {
      const list = buildEmojiListFromCategory(data, cat)
      if (list.length) {
        out.push({ title: String(cat.name || 'Emojis'), emojis: list })
      }
    }

    return out
  }, [activeTab, customTokens, data, query, recents, searchResults])

  const shouldShowSearch = activeTab !== 'custom'
  const isCustomTab = activeTab === 'custom'
  const visibleSections = React.useMemo(
    () => sections.filter((section) => section.emojis.length > 0),
    [sections],
  )
  const hasAnyEmoji = visibleSections.length > 0

  return (
    <div
      ref={rootRef}
      className={cn(
        'w-[320px] rounded-xl border border-[#272727] bg-[#111111] shadow-xl',
        className,
      )}
    >
      <div className="flex items-center gap-1 border-b border-[#272727] px-2 py-1">
        {TABS.map((tab) => {
          const Icon = tab.icon
          const active = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'relative inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground/60 hover:bg-[#1f1f1f] hover:text-foreground',
                active && 'text-foreground',
              )}
              aria-label={tab.label}
              title={tab.label}
            >
              <Icon className="h-4 w-4" />
              {active && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-1/2 h-[2px] w-5 -translate-x-1/2 rounded-full bg-foreground"
                />
              )}
            </button>
          )
        })}
      </div>

      {shouldShowSearch && (
        <div className="px-3 py-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/50" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar todos os emojis"
              className="w-full rounded-md border border-[#272727] bg-[#0b0b0b] py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-foreground/40 focus:border-sky-500 focus:outline-none"
            />
          </div>
        </div>
      )}

      <div className="max-h-[360px] overflow-y-auto px-3 pb-3">
        {!isCustomTab && loading && (
          <div className="py-10 text-center text-sm text-foreground/60">
            Carregando emojis…
          </div>
        )}

        {!isCustomTab && !loading && !data && (
          <div className="py-10 text-center text-sm text-red-400">
            Falha ao carregar biblioteca de emojis.
          </div>
        )}

        {isCustomTab && custom.loading && (
          <div className="py-10 text-center text-sm text-foreground/60">
            Carregando emojis personalizados...
          </div>
        )}

        {isCustomTab && !custom.loading && !!custom.error && (
          <div className="py-4 text-center text-sm text-red-400">
            {custom.error}
          </div>
        )}

        {((!isCustomTab && !loading && data) ||
          (isCustomTab && !custom.loading && !custom.error)) &&
          !hasAnyEmoji && (
          <div className="py-10 text-center text-sm text-foreground/60">
            Nenhum emoji encontrado.
          </div>
        )}

        {((!isCustomTab && !loading && data) ||
          (isCustomTab && !custom.loading && !custom.error)) &&
          visibleSections.map((section) => (
            <div key={section.title} className="pt-2">
              <div className="text-xs font-semibold text-foreground/70">{section.title}</div>
              <div className="mt-2 grid grid-cols-8 gap-1">
                {section.emojis.map((emoji) => {
                  const isCustomToken = isCustomTab && customByToken.has(emoji)
                  const canDelete = !!(canDeleteCustomEmojis && isCustomToken)
                  const customEmoji = isCustomToken ? customByToken.get(emoji) : null
                  const deleting = !!(customEmoji && custom.deletingId === customEmoji.id)

                  return (
                    <EmojiButton
                      key={`${section.title}:${emoji}`}
                      value={emoji}
                      onPick={handlePick}
                      deleting={deleting}
                      onDelete={
                        canDelete && customEmoji
                          ? () => {
                              if (typeof window !== 'undefined') {
                                const confirmed = window.confirm('Deletar este emoji personalizado?')
                                if (!confirmed) return
                              }
                              void custom.deleteEmoji(customEmoji)
                            }
                          : undefined
                      }
                    />
                  )
                })}
              </div>
            </div>
          ))}
      </div>

      <div className="flex items-center justify-between border-t border-[#272727] px-3 py-2">
        <div onClick={() => setActiveTab('custom')}>
          <BaseFileUpload
            accept={COMMUNITY_CUSTOM_EMOJI_ACCEPT}
            multiple={false}
            disabled={custom.uploading}
            onFiles={(files) => {
              setActiveTab('custom')
              const first = files?.[0]
              if (!first) return
              void custom.addEmoji(first)
            }}
            className="rounded-md border border-solid border-[#2a2a2a] bg-[#1a1a1a] px-3 py-1.5 text-xs font-medium text-foreground/80 hover:bg-[#202020]"
            label={
              <span className="inline-flex items-center gap-2">
                {custom.uploading && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                )}
                {custom.uploading ? 'Enviando...' : 'Adicionar emoji'}
              </span>
            }
          />
        </div>
        <span className="text-xs text-foreground/40"> </span>
      </div>
    </div>
  )
}
